import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import BpsGlobalLogo from '../Logo/BpsGlobal.jpeg';

// Helper function to load image as base64
const loadImageAsBase64 = (imagePath) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/jpeg'));
    };
    img.onerror = reject;
    img.src = imagePath;
  });
};

// Get currency code for PDF (ASCII-safe)
const getCurrencyCode = (currencyName) => {
  if (!currencyName) return 'AED';
  const lower = currencyName.toLowerCase().trim();
  const map = {
    'inr': 'INR', 'rupee': 'INR', 'indian rupee': 'INR',
    'usd': '$', 'dollar': '$', 'us dollar': '$',
    'eur': 'EUR', 'euro': 'EUR',
    'gbp': 'GBP', 'pound': 'GBP', 'british pound': 'GBP',
    'aed': 'AED', 'dirham': 'AED', 'uae dirham': 'AED',
    'sar': 'SAR', 'riyal': 'SAR', 'saudi riyal': 'SAR',
    'jpy': 'JPY', 'yen': 'JPY',
    'cny': 'CNY', 'yuan': 'CNY', 'rmb': 'CNY',
    'aud': 'A$', 'australian dollar': 'A$',
    'cad': 'C$', 'canadian dollar': 'C$',
    'sgd': 'S$', 'singapore dollar': 'S$',
    'chf': 'CHF', 'swiss franc': 'CHF',
    'myr': 'RM', 'ringgit': 'RM',
    'bdt': 'BDT', 'taka': 'BDT',
    'pkr': 'PKR', 'pakistani rupee': 'PKR',
    'omr': 'OMR', 'omani rial': 'OMR',
    'qar': 'QR', 'qatari riyal': 'QR',
    'kwd': 'KD', 'kuwaiti dinar': 'KD',
    'bhd': 'BD', 'bahraini dinar': 'BD'
  };
  if (map[lower]) return map[lower];
  for (const [key, val] of Object.entries(map)) {
    if (lower.includes(key)) return val;
  }
  return currencyName || 'AED';
};

const formatDateDDMMYYYY = (dateStr) => {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 'N/A';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

export const generateSaleReturnPDF = async (saleReturnData, companyDetails, items) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  const curr = getCurrencyCode(saleReturnData.CurrencyName);
  const fmt = (amt) => `${curr} ${parseFloat(amt || 0).toFixed(2)}`;

  // Colors
  const primary = [21, 101, 192];    // Deep blue
  const accent = [220, 53, 69];      // Red for credit note
  const textDark = [33, 37, 41];
  const textMuted = [108, 117, 125];
  const bgLight = [245, 247, 250];

  // ─── HEADER ───
  // Top accent bar
  doc.setFillColor(...primary);
  doc.rect(0, 0, pageWidth, 3, 'F');

  // Logo
  try {
    const logoBase64 = await loadImageAsBase64(BpsGlobalLogo);
    doc.addImage(logoBase64, 'JPEG', margin, 8, 22, 22);
  } catch (e) {
    doc.setFontSize(10);
    doc.setTextColor(...primary);
    doc.setFont('helvetica', 'bold');
    doc.text('BPS', margin + 5, 20);
  }

  // Company info
  doc.setFontSize(16);
  doc.setTextColor(...primary);
  doc.setFont('helvetica', 'bold');
  doc.text(companyDetails.companyName || 'BPS Global Trading', margin + 26, 16);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textMuted);
  doc.text(companyDetails.address || 'Dubai, UAE', margin + 26, 22);
  doc.text(`TRN: ${companyDetails.trn || companyDetails.vatNumber || 'N/A'}  |  Tel: ${companyDetails.phone || 'N/A'}  |  Email: ${companyDetails.email || 'N/A'}`, margin + 26, 27);

  // Document title - right aligned
  doc.setFontSize(18);
  doc.setTextColor(...accent);
  doc.setFont('helvetica', 'bold');
  doc.text('CREDIT NOTE', pageWidth - margin, 16, { align: 'right' });

  // Thin separator
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(margin, 33, pageWidth - margin, 33);

  // ─── DETAILS SECTION ───
  const detailsY = 38;

  // Left: Customer info
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...textMuted);
  doc.text('RETURN FROM', margin, detailsY);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...textDark);
  doc.text(saleReturnData.CustomerName || saleReturnData.MasterName || '-', margin, detailsY + 5);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textMuted);
  let addrY = detailsY + 10;
  if (saleReturnData.ShipToAddress1) {
    doc.text(saleReturnData.ShipToAddress1.substring(0, 50), margin, addrY);
    addrY += 4;
  }
  if (saleReturnData.ShipToAddress2) {
    doc.text(saleReturnData.ShipToAddress2.substring(0, 50), margin, addrY);
    addrY += 4;
  }
  doc.text(`TRN: ${saleReturnData.VATNumber || 'N/A'}`, margin, addrY);

  // Right: Document info table
  const infoX = pageWidth - margin - 65;
  const infoValX = pageWidth - margin;
  const infoItems = [
    ['Credit Note No:', saleReturnData.BillPrefix && saleReturnData.FinYear ? `${saleReturnData.BillPrefix}/${saleReturnData.BillNumber}/${saleReturnData.FinYear}` : (saleReturnData.BillNumber || 'N/A')],
    ['Return Date:', formatDateDDMMYYYY(saleReturnData.BillDate)],
    ['Original Invoice:', saleReturnData.SaleNo || 'N/A'],
    ['Sales Date:', formatDateDDMMYYYY(saleReturnData.SaleDate)],
    ['Currency:', saleReturnData.CurrencyName || 'AED']
  ];

  doc.setFontSize(8);
  let infoY = detailsY;
  infoItems.forEach(([label, value]) => {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...textMuted);
    doc.text(label, infoX, infoY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textDark);
    doc.text(value, infoValX, infoY, { align: 'right' });
    infoY += 5;
  });

  // ─── ITEMS TABLE ───
  const tableStartY = Math.max(addrY + 6, infoY + 4);

  const tableData = (saleReturnData.saleReturnDetails || []).map((item, i) => {
    const itemName = items.find(it => it.Id === item.ItemId)?.Name || item.ItemName || 'Item';
    return [
      i + 1,
      itemName,
      item.HSNCode || '-',
      parseFloat(item.Quantity || 0).toFixed(2),
      fmt(item.Rate),
      `${parseFloat(item.VATRate || item.GSTRate || 0).toFixed(0)}%`,
      fmt(item.TaxableAmount),
      fmt(item.VATAmount),
      fmt(item.TotalAmount)
    ];
  });

  autoTable(doc, {
    startY: tableStartY,
    head: [['#', 'Description', 'HSN', 'Qty', 'Rate', 'VAT%', 'Taxable', 'VAT', 'Total']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: primary,
      textColor: [255, 255, 255],
      fontSize: 7.5,
      fontStyle: 'bold',
      halign: 'center',
      cellPadding: 2
    },
    bodyStyles: {
      fontSize: 7.5,
      textColor: textDark,
      cellPadding: 1.8
    },
    columnStyles: {
      0: { halign: 'center', cellWidth: 8 },
      1: { halign: 'left', cellWidth: 45 },
      2: { halign: 'center', cellWidth: 14 },
      3: { halign: 'center', cellWidth: 12 },
      4: { halign: 'right', cellWidth: 22 },
      5: { halign: 'center', cellWidth: 12 },
      6: { halign: 'right', cellWidth: 22 },
      7: { halign: 'right', cellWidth: 20 },
      8: { halign: 'right', cellWidth: 25 }
    },
    alternateRowStyles: { fillColor: bgLight },
    margin: { left: margin, right: margin },
    tableLineColor: [220, 220, 220],
    tableLineWidth: 0.1
  });

  // ─── SUMMARY SECTION ───
  const summaryY = doc.lastAutoTable.finalY + 8;
  const summaryWidth = 80;
  const summaryX = pageWidth - margin - summaryWidth;

  // Build summary rows
  const rows = [
    { label: 'Subtotal', value: fmt(saleReturnData.TaxableAmount), bold: false },
    { label: 'VAT Amount', value: fmt(saleReturnData.VATAmount), bold: false }
  ];
  if (parseFloat(saleReturnData.FreightAmount || 0) > 0) {
    rows.push({ label: 'Freight', value: fmt(saleReturnData.FreightAmount), bold: false });
    rows.push({ label: 'Freight VAT', value: fmt(saleReturnData.FreightVATAmount), bold: false });
  }
  if (parseFloat(saleReturnData.PackingAmount || 0) > 0) {
    rows.push({ label: 'Packing', value: fmt(saleReturnData.PackingAmount), bold: false });
    rows.push({ label: 'Packing VAT', value: fmt(saleReturnData.PackingVATAmount), bold: false });
  }
  if (parseFloat(saleReturnData.RoundOff || 0) !== 0) {
    rows.push({ label: 'Round Off', value: fmt(saleReturnData.RoundOff), bold: false });
  }

  // Draw summary using autoTable for clean alignment
  autoTable(doc, {
    startY: summaryY,
    body: [
      ...rows.map(r => [r.label, r.value]),
      [{ content: 'CREDIT TOTAL', styles: { fontStyle: 'bold', fontSize: 9 } }, { content: fmt(saleReturnData.TotalAmount), styles: { fontStyle: 'bold', fontSize: 9 } }]
    ],
    theme: 'plain',
    styles: { fontSize: 8, textColor: textDark, cellPadding: 1.5 },
    columnStyles: {
      0: { halign: 'left', cellWidth: 35 },
      1: { halign: 'right', cellWidth: 45 }
    },
    margin: { left: summaryX, right: margin },
    didDrawCell: (data) => {
      // Draw line above grand total row
      if (data.row.index === rows.length && data.column.index === 0) {
        doc.setDrawColor(...primary);
        doc.setLineWidth(0.4);
        doc.line(summaryX, data.cell.y, summaryX + summaryWidth, data.cell.y);
      }
    }
  });

  // Payment details (if any)
  if (saleReturnData.CashAmount > 0 || saleReturnData.UPIAmount > 0 || saleReturnData.CardAmount > 0) {
    const payY = doc.lastAutoTable.finalY + 3;
    const payRows = [];
    if (saleReturnData.CashAmount > 0) payRows.push(['Cash', fmt(saleReturnData.CashAmount)]);
    if (saleReturnData.UPIAmount > 0) payRows.push(['UPI', fmt(saleReturnData.UPIAmount)]);
    if (saleReturnData.CardAmount > 0) payRows.push(['Card', fmt(saleReturnData.CardAmount)]);
    if (saleReturnData.BalanceAmount > 0) payRows.push([{ content: 'Balance', styles: { textColor: accent } }, { content: fmt(saleReturnData.BalanceAmount), styles: { textColor: accent, fontStyle: 'bold' } }]);

    autoTable(doc, {
      startY: payY,
      head: [['Refund Method', 'Amount']],
      body: payRows,
      theme: 'plain',
      headStyles: { fontSize: 7.5, fontStyle: 'bold', textColor: textMuted, cellPadding: 1.2 },
      styles: { fontSize: 8, textColor: textDark, cellPadding: 1.2 },
      columnStyles: { 0: { halign: 'left', cellWidth: 35 }, 1: { halign: 'right', cellWidth: 45 } },
      margin: { left: summaryX, right: margin }
    });
  }

  // Remarks on left side
  if (saleReturnData.Remarks) {
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textMuted);
    doc.text('Remarks:', margin, summaryY + 2);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...textDark);
    doc.text(saleReturnData.Remarks.substring(0, 60), margin, summaryY + 6);
  }

  // ─── FOOTER ───
  const footerY = pageHeight - 25;
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(margin, footerY, pageWidth - margin, footerY);

  // Signature lines
  doc.setDrawColor(...textMuted);
  doc.setLineWidth(0.2);
  doc.line(margin, footerY + 12, margin + 45, footerY + 12);
  doc.line(pageWidth - margin - 45, footerY + 12, pageWidth - margin, footerY + 12);

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textMuted);
  doc.text('Authorized Signatory', margin, footerY + 16);
  doc.text('Customer Signatory', pageWidth - margin - 45, footerY + 16);

  // Footer note
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'italic');
  doc.text('This is a computer-generated document. No signature required.', pageWidth / 2, footerY + 22, { align: 'center' });

  return doc;
};

export const downloadSaleReturnPDF = async (saleReturnData, companyDetails, items) => {
  const doc = await generateSaleReturnPDF(saleReturnData, companyDetails, items);
  const billNo = saleReturnData.BillPrefix && saleReturnData.FinYear
    ? `${saleReturnData.BillPrefix}_${saleReturnData.BillNumber}_${saleReturnData.FinYear}`
    : `CreditNote_${saleReturnData.BillNumber}`;
  doc.save(`${billNo}.pdf`);
};

export const printSaleReturnPDF = async (saleReturnData, companyDetails, items) => {
  const doc = await generateSaleReturnPDF(saleReturnData, companyDetails, items);
  doc.autoPrint();
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const printWindow = window.open(pdfUrl, '_blank');
  if (printWindow) {
    printWindow.onload = function () {
      setTimeout(() => { printWindow.print(); }, 250);
    };
    setTimeout(() => { URL.revokeObjectURL(pdfUrl); }, 10000);
  } else {
    const billNo = saleReturnData.BillPrefix && saleReturnData.FinYear
      ? `${saleReturnData.BillPrefix}_${saleReturnData.BillNumber}_${saleReturnData.FinYear}`
      : `CreditNote_${saleReturnData.BillNumber}`;
    doc.save(`${billNo}.pdf`);
    URL.revokeObjectURL(pdfUrl);
  }
};

export const getSaleReturnPDFBlob = async (saleReturnData, companyDetails, items) => {
  const doc = await generateSaleReturnPDF(saleReturnData, companyDetails, items);
  return doc.output('blob');
};
